import { ArrowLeft, CheckIcon, Close } from 'assets/icons';
import BannerTitle from 'components/banner/bannertitle';
import Button from 'components/button';
import EmptyState from 'components/EmptyState/EmptyState';
import UseModal from 'components/modal/useModal';
import StyledInput from 'components/styledInput/StyledInput';
import SubNavbar from 'components/SubNavbar/SubNavbar';
import Tooltip from 'components/tooltips/Tooltips';
import {
  dateWordMonth,
  formatDurationFromHoursString,
  formatDurationFromMinutes,
  parseHoursToMinutes,
} from 'helper/helper';
import TimeSheetHistoryList from 'components/timeSheetHistory/TimeSheetHistoryList';
import UseContext from 'hooks/useContext';
import useImportedTimeSheet from 'hooks/useImportedTimeSheet';
import useScrolledShadow from 'hooks/useScrolledShadow';
import useTimeSheetHistory from 'hooks/useTimeSheetHistory';
import useSlideOver from 'components/SlideOver/useSlideOver';
import TBodyHoursCompany from 'pages/timeSheet/components/tBodyHours';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import MediaQuery from 'react-responsive';
import PostIt from 'components/postIt/PostIt';

// ── Skeleton ──

const FicheHourSkeleton = () => (
  <div className="flex flex-col gap-6 items-center justify-center tab:flex-row">
    <div className="w-full tab:max-w-[654px] bg-white flex flex-col gap-4 p-6">
      <div className="h-8 skeleton-bar w-64 mb-4" />
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 skeleton-bar" />
        ))}
      </div>
    </div>
    <MediaQuery minWidth={1100}>
      <div className="w-full tab:w-[450px] bg-white rounded-xl border border-gray-100 p-6">
        <div className="h-6 skeleton-bar w-32 mb-6" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-5 skeleton-bar" />
          ))}
        </div>
      </div>
    </MediaQuery>
  </div>
);

// ── Info Card (shared mobile/desktop) ──

const STATUS_LABELS = { 0: 'Non complété', 1: 'À vérifier', 2: 'Litige', 3: 'Vérifié' };
const STATUS_LIST = [
  { label: 'Non complété', value: 0 },
  { label: 'À vérifier', value: 1 },
  { label: 'Litige', value: 2 },
  { label: 'Vérifié', value: 3 },
];

/** API / aggregation may return validated as number or string ("0", "3", …). */
const normalizeTimeSheetValidated = (v) => {
  const n = Number(v);
  return [0, 1, 2, 3].includes(n) ? n : 0;
};

const isImportedFlag = (value) => value === true || value === 'true' || value === 1;

/**
 * Defined at module scope on purpose. Declared inside TimeSheetInfoCard, these
 * became a new component type on every render, so React remounted the subtree
 * and reset the Tooltip's own state — the tooltip vanished as soon as anything
 * re-rendered the card.
 */
const InfoRow = ({ label, children }) => (
  <div className="flex justify-between items-center text-sm">
    <span className="font-bold text-gray-900">{label}</span>
    {children}
  </div>
);

const ClickableValue = ({ text, tooltip, onClick }) => (
  <Tooltip text={tooltip} placement="top">
    <span
      className="text-gray-500 font-medium text-right max-w-[200px] truncate cursor-pointer hover:underline decoration-dashed transition-colors"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {text}
    </span>
  </Tooltip>
);

const StaticValue = ({ text }) => (
  <span className="text-gray-500 font-medium text-right max-w-[200px] truncate">
    {text}
  </span>
);

export const TimeSheetInfoCard = ({ data, missionTitle, contentTitle, onOpenTempWorker, onOpenMission, className = '', cardTitle = 'Informations' }) => {
  const contractId = data?.mission?.contract_company?.[0]?.id_bestt || data?.mission?.contract_tempworker?.[0]?.id_bestt;
  const userId = data?.id_user || data?.user?.id_user;
  const hasMission = data?.id_mission || data?.id_mission_reel;

  const fullName = `${data?.user?.firstname || ''} ${data?.user?.lastname || ''}`.trim() || '-';
  const title = data?.mission?.title || missionTitle || contentTitle || '-';

  return (
    <div className={`w-full bg-white rounded-xl border border-gray-100 p-6 ${className}`}>
      <h3 className="font-bold text-lg text-gray-900 mb-6">{cardTitle}</h3>
      <div className="flex flex-col gap-6">
        <InfoRow label="Période">
          <StaticValue text={`${dateWordMonth(data?.periode_start)} > ${dateWordMonth(data?.periode_end)}`} />
        </InfoRow>
        <InfoRow label="Intérimaire">
          {userId
            ? <ClickableValue text={fullName} tooltip="Voir la fiche" onClick={() => onOpenTempWorker(userId)} />
            : <StaticValue text={fullName} />
          }
        </InfoRow>
        <InfoRow label="Mission">
          {hasMission
            ? <ClickableValue text={title} tooltip="Voir la mission" onClick={onOpenMission} />
            : <StaticValue text={title} />
          }
        </InfoRow>
        <InfoRow label="Contrat">
          <StaticValue text={contractId ? `n°${contractId}` : '-'} />
        </InfoRow>
      </div>
    </div>
  );
};

const CENTIEMES_STORAGE_KEY = 'ficheHour-centiemes-display';

// ── Recap Card (Détail du relevé) ──

const timeToMinutes = (t) => {
  if (!t || typeof t !== 'string' || !t.includes(':')) return null;
  const [h, m] = t.split(':');
  const hh = parseInt(h, 10);
  const mm = parseInt(m, 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

const overlapMinutes = (start, end, from, to) =>
  Math.max(0, Math.min(end, to) - Math.max(start, from));

const nightMinutesForPeriod = (start, end, nightStartMin, nightEndMin) => {
  const s = timeToMinutes(start);
  const e = timeToMinutes(end);
  if (s === null || e === null || e <= s) return 0;
  if (nightStartMin === nightEndMin) return 0;

  // Only a window that wraps past midnight — 21h → 6h — splits into an evening
  // and an early-morning segment. A window held within the day, such as
  // 10h → 13h, is a plain interval; treating it as wrapping counted everything
  // outside it instead, and counted the middle twice.
  if (nightStartMin < nightEndMin) {
    return overlapMinutes(s, e, nightStartMin, nightEndMin);
  }
  return (
    overlapMinutes(s, e, 0, nightEndMin) +
    overlapMinutes(s, e, nightStartMin, 24 * 60)
  );
};

const resolveNightWindow = (company) => {
  const nh = company?.nightHours?.[0];
  if (!nh || !Number.isFinite(nh.start) || !Number.isFinite(nh.end)) return null;
  return { startMin: nh.start * 60, endMin: nh.end * 60 };
};

const resolveOvertimeThreshold = (company) => {
  const ot = company?.overtimes?.[0];
  if (!ot || !Number.isFinite(ot.start)) return null;
  return ot.start * 60;
};

const computeTimeSheetRecap = (record) => {
  const empty = { totalMin: 0, nightMin: 0, overtimeMin: 0, hasNight: false, hasOvertime: false };
  if (!record) return empty;
  const totalMin = parseHoursToMinutes(record.total_hours_ent);
  const nightWindow = resolveNightWindow(record.company);
  const overtimeThresholdMin = resolveOvertimeThreshold(record.company);
  const hasNight = !!nightWindow;
  const hasOvertime = overtimeThresholdMin !== null;

  let nightMin = 0;
  if (hasNight) {
    (record.days || []).forEach((day) => {
      ['periode_one', 'periode_two', 'periode_three'].forEach((key) => {
        const company = day[`${key}_company`];
        const original = day[key];
        const useCompany = company?.start && company.start !== '' && company.start !== '00:00';
        const period = useCompany ? company : original;
        if (period?.start && period?.end && period.start !== '00:00') {
          nightMin += nightMinutesForPeriod(period.start, period.end, nightWindow.startMin, nightWindow.endMin);
        }
      });
    });
  }
  const overtimeMin = hasOvertime ? Math.max(0, totalMin - overtimeThresholdMin) : 0;
  return { totalMin, nightMin, overtimeMin, hasNight, hasOvertime };
};

/** Module scope, same reason as InfoRow above: no remount on every render. */
const Row = ({ label, value }) => (
  <div className="flex justify-between items-center text-sm">
    <span className="font-bold text-gray-900">{label}</span>
    <span className="text-gray-500 font-medium text-right">{value}</span>
  </div>
);

export const TimeSheetRecapCard = ({ data, onAddIncreases, statusSelector, showCentiemes = false, className = '' }) => {
  const { totalMin, nightMin, overtimeMin, hasNight, hasOvertime } = useMemo(
    () => computeTimeSheetRecap(data),
    [data]
  );

  let bannerTitle = null;
  let bannerDescription = null;
  let bannerActionLabel = null;
  if (!hasNight && !hasOvertime) {
    bannerTitle = 'Majorations manquantes';
    bannerDescription = 'Ajoutez les heures de nuit & les heures supplémentaires pour voir le reste des informations';
    bannerActionLabel = 'Ajouter les majorations';
  } else if (!hasNight) {
    bannerTitle = 'Heures de nuit manquantes';
    bannerDescription = 'Ajoutez les heures de nuit pour voir le reste des informations';
    bannerActionLabel = 'Ajouter les heures de nuit';
  } else if (!hasOvertime) {
    bannerTitle = 'Heures supplémentaires manquantes';
    bannerDescription = 'Ajoutez les heures supplémentaires pour voir le reste des informations';
    bannerActionLabel = 'Ajouter les heures supplémentaires';
  }

  return (
    <div className={`w-full bg-white rounded-xl border border-gray-100 p-6 ${className}`}>
      <h3 className="font-bold text-lg text-gray-900 mb-6">Détail du relevé</h3>
      <div className="flex flex-col gap-6">
        {statusSelector && (
          <div className="flex justify-between items-center text-sm">
            <span className="font-bold text-gray-900">Statut</span>
            <div className="max-w-[200px] w-full shrink-0">
              {statusSelector}
            </div>
          </div>
        )}
        <Row label="Heures totales" value={formatDurationFromMinutes(totalMin, showCentiemes)} />
        {hasNight && <Row label="Heures de nuit" value={formatDurationFromMinutes(nightMin, showCentiemes)} />}
        {hasOvertime && <Row label="Heures supplémentaires" value={formatDurationFromMinutes(overtimeMin, showCentiemes)} />}
        {bannerTitle && (
          <BannerTitle
            type="warning"
            title={bannerTitle}
            action={onAddIncreases ? { label: bannerActionLabel, onClick: onAddIncreases } : undefined}
          >
            {bannerDescription}
          </BannerTitle>
        )}
      </div>
    </div>
  );
};

// ── Detail / Activity panel ──

const PANEL_TABS = [
  { key: 'detail', label: 'Détail' },
  { key: 'activity', label: 'Activité' },
];

/**
 * The sheet's side panel: what it is on one tab, what was done to it on the
 * other. Written once and placed by width, so the two media queries no longer
 * carry their own copy of the cards.
 */
export const TimeSheetDetailPanel = ({
  record,
  tab,
  onTabChange,
  missionCompany,
  onPostItSaved,
  missionTitle,
  contentTitle,
  onOpenTempWorker,
  onOpenMission,
  onAddIncreases,
  statusSelector,
  showCentiemes,
  history,
  className = '',
}) => (
  <div className={`w-full flex flex-col gap-4 ${className}`}>
    <div className="flex gap-2 border-b border-gray-200">
      {PANEL_TABS.map(({ key, label }) => (
        <div
          key={key}
          className={`cursor-pointer pt-1 pb-2 px-2 border-b ${
            tab === key
              ? 'border-yellow-500 text-yellow-500'
              : 'hover:border-yellow-500 hover:text-yellow-500 text-gray-500'
          }`}
          onClick={() => onTabChange(key)}
        >
          {label}
        </div>
      ))}
    </div>

    {tab === 'detail' ? (
      <>
        <PostIt
          note={missionCompany?.post_it}
          companyName={
            missionCompany?.name_commercial ?? missionCompany?.reason_social
          }
          companyId={missionCompany?._id}
          onSaved={onPostItSaved}
        />
        <TimeSheetInfoCard
          data={record}
          missionTitle={missionTitle}
          contentTitle={contentTitle}
          onOpenTempWorker={onOpenTempWorker}
          onOpenMission={onOpenMission}
        />
        <TimeSheetRecapCard
          data={record}
          onAddIncreases={onAddIncreases}
          statusSelector={statusSelector}
          showCentiemes={showCentiemes}
        />
      </>
    ) : (
      <div className="w-full min-h-0 flex-1 flex flex-col bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="font-bold text-lg text-gray-900 mb-2">Activité</h3>
        <p className="ft-sm text-gray-500 mb-4">
          Actions enregistrées sur ce relevé, de la plus récente à la plus
          ancienne.
        </p>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <TimeSheetHistoryList
            events={history.events}
            isLoading={history.isLoading}
            error={history.error}
          />
        </div>
      </div>
    )}
  </div>
);

// ── Component ──

const FicheHour = () => {
  const {
    setObjID, setObjType, refreshTime, setTotalH, content,
    timeCheck, setTimeCheck, setUpdatedTimesheet, setMissionInitialPage,
    setTarget, userData, setModalData, modalDataReturn, setModalDataReturn,
    setModalType, setModalShow, updatedTimesheet,
  } = UseContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toggleSlideOver } = useSlideOver();
  const [panelTab, setPanelTab] = useState('detail');
  const { ref: pageScrollRef, isScrolled: isPageScrolled } = useScrolledShadow();
  const [datas, setDatas] = useState([]);
  const [nMission, setNMission] = useState('');
  // The company of the mission, for its note. getInfo unwinds the lookup, so
  // this is a plain object, not an array.
  const [missionCompany, setMissionCompany] = useState(null);
  const updateMissionCompanyPostIt = (nextNote) =>
    setMissionCompany((company) =>
      company ? { ...company, post_it: nextNote } : company,
    );
  const { toggle } = UseModal();
  const [isTimeSheetImported, setIsTimeSheetImported] = useState(false);
  const [showCentiemes, setShowCentiemes] = useState(
    () => localStorage.getItem(CENTIEMES_STORAGE_KEY) === 'true'
  );
  const [isWaiting, setIsWaiting] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const token = localStorage.getItem('xsrfToken');
  const { toggleImported } = useImportedTimeSheet();

  /** Hands the flag to the time sheet list, which caches its rows across navigations. */
  const publishImported = (idTimeSheet, imported) =>
    setUpdatedTimesheet((prev) =>
      prev[idTimeSheet] === imported ? prev : { ...prev, [idTimeSheet]: imported }
    );

  useEffect(() => {
    setIsWaiting(true);
    ApiDatabase.getTimeSheetInfo(
      { id },
      (data) => {
        const sheet = data[0];
        setDatas(data);
        setObjID(sheet.id_time_sheet);
        // The sheet just came from the server: it wins over whatever the list
        // published earlier, and it refreshes that entry for the list itself,
        // which stays mounted behind this page with its own older copy.
        const imported = isImportedFlag(sheet.is_imported_bestt);
        setIsTimeSheetImported(imported);
        publishImported(sheet._id, imported);
        setIsWaiting(false);
      },
      (err) => { if (err.name !== 'CanceledError') { console.log(err); setIsWaiting(false); } }
    );
  }, [refreshTime, id]);

  useEffect(() => {
    if (datas.length > 0 && datas[0]?.id_mission) {
      ApiDatabase.getInfoMission(
        { id: datas[0].id_mission },
        (data) => {
          setNMission(data.title);
          setObjType(data.title);
          setMissionCompany(data.infoCompany ?? null);
        },
        (err) => { if (err.name !== 'CanceledError') console.log(err); }
      );
    }
  }, [datas[0]?.id_mission]);

  const getSeconds = (s) => s.split('h').reduce((acc, curr) => acc * 60 + +curr, 0);

  const { allDaysValid, allTimeSheetValid, allTimeSheetClose } = useMemo(() => {
    const days = datas[0]?.days || [];
    let totalSeconds = 0;
    let valid = true;
    days.forEach((d) => {
      if (d.day_hours_ent) totalSeconds += getSeconds(d.day_hours_ent);
      if (normalizeTimeSheetValidated(d.validated) === 0) valid = false;
    });
    setTotalH(totalSeconds);

    const checkValues = Object.values(timeCheck);
    const hasChecks = checkValues.length > 0;
    return {
      allDaysValid: valid,
      allTimeSheetValid: hasChecks && checkValues.every((v) => v === 2),
      allTimeSheetClose: hasChecks && checkValues.every((v) => v === 3),
    };
  }, [datas, timeCheck]);

  const handleSubmit = () => {
    if (allDaysValid) toggle('doc_u_validWeek');
    else toast.error('Veuillez compléter la semaine');
  };

  const toggleAll = (targetState) => {
    const allAlready = Object.values(timeCheck).every((v) => v === targetState);
    const newVal = allAlready ? '' : targetState;
    const updated = {};
    for (const key in timeCheck) updated[key] = newVal;
    setTimeCheck(updated);
  };

  const handleNext = (e) => {
    e.preventDefault();
    const allSet = Object.values(timeCheck).every((v) => v !== '');
    if (allSet) { setObjID(id); toggle('doc_u_validWeek'); }
    else toast.error('Veuillez valider ou refuser toutes les heures.');
  };

  useEffect(() => {
    const initial = {};
    datas[0]?.days?.forEach((i) => { initial[i._id] = ''; });
    setTimeCheck(initial);
  }, [datas]);

  const record = datas[0];

  // The history is only worth a request once someone asks for it, and it stays
  // loaded afterwards so switching back to it costs nothing.
  const [activityOpened, setActivityOpened] = useState(false);
  const history = useTimeSheetHistory(activityOpened ? record?._id : null);

  const openPanelTab = (key) => {
    if (key === 'activity') setActivityOpened(true);
    setPanelTab(key);
  };

  const validatedNum = normalizeTimeSheetValidated(record?.validated);
  const isSheetEditable = validatedNum === 1 || validatedNum === 2;
  const canEditTimeSheetStatus = userData?.role === 500;

  const openStatusConfirmModal = (targetStatus, confirmReason) => {
    setModalData({ targetStatus, confirmReason });
    setModalType('ts_confirm_status_change');
    setModalShow(true);
  };

  const handleToggleCentiemes = (checked) => {
    setShowCentiemes(checked);
    localStorage.setItem(CENTIEMES_STORAGE_KEY, String(checked));
  };

  const handleToggleImported = (isImported, idTimeSheet) => {
    setIsTimeSheetImported(isImported);
    toggleImported(
      isImported,
      idTimeSheet,
      (imported) => {
        setDatas((prev) => prev.map((item) => item._id === idTimeSheet ? { ...item, is_imported_bestt: imported } : item));
        setIsTimeSheetImported(imported);
        publishImported(idTimeSheet, imported);
      },
      () => {
        setIsTimeSheetImported(!isImported);
        toast.error('Erreur lors de la mise à jour du statut importé.');
      }
    );
  };

  const performStatusChange = (targetStatus) => {
    const currentRecord = datas[0];
    if (!currentRecord) return;
    setStatusLoading(true);
    ApiDatabase.updateTimeSheetStatus(
      { id: currentRecord._id, validated: Number(targetStatus), token },
      () => {
        const wasImported = isTimeSheetImported;
        setDatas((prev) => prev.map((item) =>
          item._id === currentRecord._id
            ? {
              ...item,
              validated: targetStatus,
              ...(wasImported ? { is_imported_bestt: false } : {}),
            }
            : item
        ));
        if (wasImported) {
          setIsTimeSheetImported(false);
          setUpdatedTimesheet((prev) => ({ ...prev, [currentRecord._id]: false }));
        }
        setStatusLoading(false);
        toast.success('Statut mis à jour.');
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          toast.error('Erreur lors de la mise à jour du statut.');
        }
        setStatusLoading(false);
      }
    );
  };

  const handleStatusChange = (selectedList) => {
    if (!canEditTimeSheetStatus) return;
    const selected = selectedList.find((item) => item.checked);
    const currentRecord = datas[0];
    if (!selected || !currentRecord) return;

    const isCurrentlyIncomplete = normalizeTimeSheetValidated(currentRecord.validated) === 0;
    const hasEmptyDays = (currentRecord.days || []).some(
      (d) => normalizeTimeSheetValidated(d.validated) === 0
    );
    if (selected.value === validatedNum) return;

    if (isCurrentlyIncomplete && hasEmptyDays && selected.value !== 0) {
      openStatusConfirmModal(selected.value, 'incomplete');
      return;
    }

    if (isTimeSheetImported) {
      openStatusConfirmModal(selected.value, 'imported');
      return;
    }

    performStatusChange(selected.value);
  };

  useEffect(() => {
    if (modalDataReturn?.type === 'ts_status_change_confirmed') {
      performStatusChange(modalDataReturn.targetStatus);
      setModalDataReturn(null);
    }
  }, [modalDataReturn]);

  const statusSelector = canEditTimeSheetStatus ? (
    <StyledInput
      type="selectMultipleRadio"
      value={STATUS_LABELS[validatedNum]}
      list={STATUS_LIST.map((item) => ({ ...item, checked: item.value === validatedNum }))}
      onChange={handleStatusChange}
      isDisabled={statusLoading}
      className="w-full border rounded-md bg-white text-gray-900 ft-sm"
      classNameValue="px-4 py-2 flex items-center justify-end text-right truncate"
    />
  ) : (
    <span className="text-gray-500 font-medium text-right max-w-[200px] truncate block">
      {STATUS_LABELS[validatedNum]}
    </span>
  );

  const openTempWorkerInfo = (userId) => {
    setObjID(userId);
    toggleSlideOver('get_temp_worker');
  };

  const openCompanyRemuneration = () => {
    const companyId = datas[0]?.company?._id || datas[0]?.mission?.company;
    if (!companyId) return;
    setObjID(companyId);
    if (setTarget) setTarget('remuneration_conditions');
    toggleSlideOver('get_company');
  };

  const openMissionInfo = () => {
    if (setMissionInitialPage) setMissionInitialPage(3);
    if (datas[0]?.id_mission) {
      setObjID(datas[0].id_mission);
      toggleSlideOver('get_mission');
    } else if (datas[0]?.id_mission_reel) {
      ApiDatabase.searchMission(
        { token, search: datas[0].id_mission_reel.toString(), optionSearch: 'id_mission' },
        (data) => {
          if (data.missions?.length > 0) {
            const mission = data.missions.find(m => m.id_mission?.toString() === datas[0].id_mission_reel?.toString());
            const missionId = (mission || data.missions[0])?.id_mission_real;
            if (missionId) { setObjID(missionId); toggleSlideOver('get_mission'); }
          }
        },
        (err) => console.error('Error fetching mission:', err)
      );
    }
  };

  return (
    <div className="flex flex-col w-full h-full motion-safe:animate-contentIn">
      <SubNavbar elevated={isPageScrolled}>
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/timeSheet'); }}
        >
          <ArrowLeft w={16} color="#111827" />
          <div className="text-sm font-medium text-gray-900">Retour</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="border rounded py-2 px-4 text-sm centiemes-button-fiche-hour">
            <label className="flex items-center gap-2 cursor-pointer">
              <StyledInput
                type="checkbox"
                checked={showCentiemes}
                onChange={(e) => handleToggleCentiemes(e.target.checked)}
              />
              <div>Centièmes</div>
            </label>
          </div>
          {validatedNum === 3 && (
            <div className="border rounded py-2 px-4 text-sm import-button-fiche-hour">
              <label className="flex items-center gap-2 cursor-pointer">
                <StyledInput
                  type="checkbox"
                  checked={isTimeSheetImported}
                  onChange={(e) => handleToggleImported(e.target.checked, record?._id)}
                />
                <div>Importé</div>
              </label>
            </div>
          )}
        </div>
      </SubNavbar>

      <div ref={pageScrollRef} className="flex-1 overflow-y-auto p-3 tab:p-4 lg:p-8">
        {isWaiting ? (
          <FicheHourSkeleton />
        ) : record ? (
          <div className="flex flex-col gap-12 items-center justify-center tab:flex-row tab:items-stretch">
            <div className="w-full tab:max-w-[654px] bg-white flex flex-col gap-4 header-fiche-hour">
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  Relevé d'heure n°{record?.id_time_sheet}
                </h1>
              </div>

              {/* Detail / Activity — mobile/tablet */}
              <MediaQuery maxWidth={1099}>
                <TimeSheetDetailPanel
                  record={record}
                  tab={panelTab}
                  onTabChange={openPanelTab}
                  missionCompany={missionCompany}
                  onPostItSaved={updateMissionCompanyPostIt}
                  missionTitle={nMission}
                  contentTitle={content?.title}
                  onOpenTempWorker={openTempWorkerInfo}
                  onOpenMission={openMissionInfo}
                  onAddIncreases={openCompanyRemuneration}
                  statusSelector={statusSelector}
                  showCentiemes={showCentiemes}
                  history={history}
                />
              </MediaQuery>

              <div className="flex items-center justify-between mb-4 px-2">
                <div className="flex gap-6 items-center ft-sm text-gray-700 font-medium">
                  JOUR
                  <span className="text-gray-500">
                    {record?.total_hours_ent
                      ? `heures travaillées (${formatDurationFromHoursString(record.total_hours_ent, showCentiemes)})`
                      : null}
                  </span>
                </div>
                {isSheetEditable && (
                  <div className="flex items-center justify-end gap-2">
                    {validatedNum !== 2 && (
                      <Button
                        color={allTimeSheetValid ? 'REFUSED' : 'ICON'}
                        size="ICON"
                        onClick={() => toggleAll(2)}
                      >
                        <Close wh={20} color={allTimeSheetValid ? '#B91C1C' : '#374151'} />
                      </Button>
                    )}
                    <Button
                      color={allTimeSheetClose ? 'DEFAULT' : 'ICON'}
                      size="ICON"
                      onClick={() => toggleAll(3)}
                      className={allTimeSheetClose ? 'bg-green-100 hover:bg-green-200' : ''}
                    >
                      <CheckIcon w={15} color={allTimeSheetClose ? '#047857' : '#374151'} />
                    </Button>
                  </div>
                )}
              </div>

              <TBodyHoursCompany champsBody={record} showCentiemes={showCentiemes} />

              {isSheetEditable && (
                <div className="flex">
                  <div className="ml-auto">
                    <Button size="LG" onClick={handleNext}>Confirmer</Button>
                  </div>
                </div>
              )}
            </div>

            {/* Detail / Activity — desktop */}
            <MediaQuery minWidth={1100}>
              {/* `items-stretch` equalises the columns on the taller one, it does
                  not cap them: a long activity would simply grow the row and never
                  overflow. Taking the panel out of flow leaves this wrapper with no
                  in-flow content, so the row is sized by the sheet alone and
                  `max-h-full` below is the sheet's own height. The detail tab stays
                  in flow — its cards must be free to make the column taller. */}
              <div className="tab:w-[450px] flex-shrink-0 relative">
                <TimeSheetDetailPanel
                  className={
                    panelTab === 'activity'
                      ? 'absolute inset-x-0 top-0 max-h-full'
                      : ''
                  }
                  record={record}
                  tab={panelTab}
                  onTabChange={openPanelTab}
                  missionCompany={missionCompany}
                  onPostItSaved={updateMissionCompanyPostIt}
                  missionTitle={nMission}
                  contentTitle={content?.title}
                  onOpenTempWorker={openTempWorkerInfo}
                  onOpenMission={openMissionInfo}
                  onAddIncreases={openCompanyRemuneration}
                  statusSelector={statusSelector}
                  showCentiemes={showCentiemes}
                  history={history}
                />
              </div>
            </MediaQuery>
          </div>
        ) : (
          <EmptyState type="time_sheet" />
        )}
      </div>
    </div>
  );
};

export default FicheHour;
